import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Link } from 'react-router-dom';
import axios from 'axios';
import ReactJson from 'react-json-view';
import { Plus, Edit, Trash2, TestTube, Loader2, Play, AlertCircle, ScrollText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RulesPage() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState(null);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testPayload, setTestPayload] = useState('{}');
  const [testResult, setTestResult] = useState(null);
  const [capturedWebhooks, setCapturedWebhooks] = useState([]);
  const [selectedPayload, setSelectedPayload] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    condition_groups: [],
    group_logic: 'OR',
    destination_url: '',
    http_method: 'ORIGINAL',
    custom_response: '',
    custom_response_enabled: false
  });

  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async () => {
    try {
      const response = await axios.get(`${API}/rules`);
      setRules(response.data);
    } catch (error) {
      toast.error('Gagal mengambil rules');
    } finally {
      setLoading(false);
    }
  };

  const fetchCapturedWebhooks = async () => {
    try {
      const response = await axios.get(`${API}/capture/webhooks`);
      setCapturedWebhooks(response.data);
    } catch (error) {
      console.error('Failed to fetch captured webhooks');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate that conditions (if provided) are complete
    if (formData.condition_groups && formData.condition_groups.length > 0) {
      const hasEmptyConditions = formData.condition_groups.some(group =>
        group.conditions.some(c => !c.key_to_parse || !c.value)
      );
      
      if (hasEmptyConditions) {
        toast.error('Semua kondisi harus diisi lengkap atau hapus kondisi kosong');
        return;
      }
    }
    
    try {
      if (editingRule) {
        await axios.put(`${API}/rules/${editingRule._id}`, formData);
        toast.success('Rule berhasil diupdate');
      } else {
        await axios.post(`${API}/rules`, formData);
        toast.success('Rule berhasil dibuat');
      }
      setDialogOpen(false);
      resetForm();
      fetchRules();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Gagal menyimpan rule');
    }
  };

  const handleDelete = async () => {
    if (!ruleToDelete) return;
    
    try {
      await axios.delete(`${API}/rules/${ruleToDelete._id}`);
      toast.success('Rule dan logs terkait berhasil dihapus');
      setDeleteDialogOpen(false);
      setRuleToDelete(null);
      fetchRules();
    } catch (error) {
      toast.error('Gagal menghapus rule');
    }
  };

  const handleEdit = (rule) => {
    setEditingRule(rule);
    setFormData({
      name: rule.name,
      condition_groups: rule.condition_groups || [],
      group_logic: rule.group_logic,
      destination_url: rule.destination_url,
      http_method: rule.http_method,
      custom_response: rule.custom_response || '',
      custom_response_enabled: rule.custom_response_enabled || false
    });
    setDialogOpen(true);
  };

  const handleToggleActive = async (rule) => {
    try {
      const response = await axios.patch(`${API}/rules/${rule._id}/toggle`);
      toast.success(response.data.message);
      fetchRules();
    } catch (error) {
      toast.error('Gagal mengubah status rule');
    }
  };

  const openDeleteDialog = (rule) => {
    setRuleToDelete(rule);
    setDeleteDialogOpen(true);
  };

  const openTestDialog = (rule) => {
    setEditingRule(rule);
    setTestDialogOpen(true);
    setTestResult(null);
    fetchCapturedWebhooks();
  };

  const handleTestRule = async () => {
    try {
      const payload = JSON.parse(testPayload);
      const response = await axios.post(`${API}/rules/test`, {
        rule: formData,
        payload
      });
      setTestResult(response.data);
      toast.success(response.data.message);
    } catch (error) {
      if (error instanceof SyntaxError) {
        toast.error('JSON payload tidak valid');
      } else {
        toast.error('Gagal menjalankan test');
      }
    }
  };

  const addConditionGroup = () => {
    if (formData.condition_groups.length >= 7) {
      toast.error('Maksimal 7 condition groups');
      return;
    }
    setFormData({
      ...formData,
      condition_groups: [
        ...formData.condition_groups,
        { conditions: [{ key_to_parse: '', operator: 'equals', value: '' }], logic: 'AND' }
      ]
    });
  };

  const removeConditionGroup = (groupIndex) => {
    const newGroups = formData.condition_groups.filter((_, i) => i !== groupIndex);
    setFormData({ ...formData, condition_groups: newGroups });
  };

  const addCondition = (groupIndex) => {
    const newGroups = [...formData.condition_groups];
    if (newGroups[groupIndex].conditions.length >= 7) {
      toast.error('Maksimal 7 kondisi per group');
      return;
    }
    newGroups[groupIndex].conditions.push({ key_to_parse: '', operator: 'equals', value: '' });
    setFormData({ ...formData, condition_groups: newGroups });
  };

  const removeCondition = (groupIndex, condIndex) => {
    const newGroups = [...formData.condition_groups];
    newGroups[groupIndex].conditions = newGroups[groupIndex].conditions.filter((_, i) => i !== condIndex);
    if (newGroups[groupIndex].conditions.length === 0) {
      newGroups.splice(groupIndex, 1);
    }
    setFormData({ ...formData, condition_groups: newGroups });
  };

  const updateCondition = (groupIndex, condIndex, field, value) => {
    const newGroups = [...formData.condition_groups];
    newGroups[groupIndex].conditions[condIndex][field] = value;
    setFormData({ ...formData, condition_groups: newGroups });
  };

  const updateGroupLogic = (groupIndex, logic) => {
    const newGroups = [...formData.condition_groups];
    newGroups[groupIndex].logic = logic;
    setFormData({ ...formData, condition_groups: newGroups });
  };

  const handleJsonSelect = (select) => {
    // When user clicks on a key in JSON viewer, set it as key_to_parse
    if (select && select.namespace && select.namespace.length > 0) {
      const path = select.namespace.join('.');
      // Set to the first empty condition
      const firstEmptyGroup = formData.condition_groups.findIndex(g =>
        g.conditions.some(c => !c.key_to_parse)
      );
      if (firstEmptyGroup !== -1) {
        const firstEmptyCond = formData.condition_groups[firstEmptyGroup].conditions.findIndex(
          c => !c.key_to_parse
        );
        if (firstEmptyCond !== -1) {
          updateCondition(firstEmptyGroup, firstEmptyCond, 'key_to_parse', path);
          toast.success(`Key path selected: ${path}`);
        }
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      condition_groups: [],
      group_logic: 'OR',
      destination_url: '',
      http_method: 'ORIGINAL',
      custom_response: '',
      custom_response_enabled: false
    });
    setEditingRule(null);
    setSelectedPayload(null);
    setTestPayload('{}');
    setTestResult(null);
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold mb-2" data-testid="rules-page-title">Routing Rules</h1>
            <p className="text-gray-600">Konfigurasi webhook forwarding rules</p>
          </div>
          <Button onClick={() => setDialogOpen(true)} data-testid="create-rule-btn">
            <Plus className="w-4 h-4 mr-2" />
            Buat Rule
          </Button>
        </div>

        <Card data-testid="rules-table-card">
          <CardHeader>
            <CardTitle>Semua Rules ({rules.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin mr-2" />
                Loading...
              </div>
            ) : rules.length === 0 ? (
              <div className="text-center py-8 text-gray-500" data-testid="no-rules-message">
                Belum ada rules. Buat rule pertama untuk memulai.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nama</TableHead>
                    <TableHead>Webhook URL</TableHead>
                    <TableHead>Conditions</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead>Destination</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rules.map((rule) => (
                    <TableRow key={rule._id} data-testid={`rule-row-${rule._id}`}>
                      <TableCell className="font-medium">{rule.name}</TableCell>
                      <TableCell>
                        <div className="flex flex-col space-y-1">
                          <div className="flex items-center space-x-2">
                            <code className="text-xs bg-blue-50 px-2 py-1 rounded border border-blue-200">
                              /api/hook/{rule.webhook_url_id.substring(0, 12)}...
                            </code>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                navigator.clipboard.writeText(`${process.env.REACT_APP_BACKEND_URL}/api/hook/${rule.webhook_url_id}`);
                                toast.success('URL copied!');
                              }}
                              data-testid={`copy-url-btn-${rule._id}`}
                              title="Copy webhook URL"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </Button>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {rule.condition_groups?.length || 0} groups
                          {(!rule.condition_groups || rule.condition_groups.length === 0) && ' (forward all)'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                          {rule.http_method}
                        </code>
                      </TableCell>
                      <TableCell className="text-sm text-gray-600 max-w-xs truncate">
                        {rule.destination_url}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant={rule.active ? 'default' : 'secondary'}
                          size="sm"
                          onClick={() => handleToggleActive(rule)}
                          data-testid={`toggle-active-btn-${rule._id}`}
                        >
                          {rule.active ? 'Active' : 'Inactive'}
                        </Button>
                      </TableCell>
                      <TableCell className="text-right space-x-1">
                        <Link to={`/logs?rule_id=${rule._id}`}>
                          <Button
                            variant="ghost"
                            size="sm"
                            title="View logs for this rule"
                            data-testid={`view-logs-btn-${rule._id}`}
                          >
                            <ScrollText className="w-4 h-4 text-purple-600" />
                          </Button>
                        </Link>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openTestDialog(rule)}
                          data-testid={`test-rule-btn-${rule._id}`}
                        >
                          <TestTube className="w-4 h-4 text-green-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(rule)}
                          data-testid={`edit-rule-btn-${rule._id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDeleteDialog(rule)}
                          data-testid={`delete-rule-btn-${rule._id}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Rule Dialog */}
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="rule-dialog">
            <DialogHeader>
              <DialogTitle>{editingRule ? 'Edit Rule' : 'Buat Rule Baru'}</DialogTitle>
              <DialogDescription>
                Konfigurasikan condition dan destination untuk webhook forwarding
              </DialogDescription>
            </DialogHeader>
            
            <Tabs defaultValue="basic" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="basic">Basic Info</TabsTrigger>
                <TabsTrigger value="conditions">Conditions</TabsTrigger>
                <TabsTrigger value="test">Test Payload</TabsTrigger>
              </TabsList>
              
              <TabsContent value="basic" className="space-y-4">
                <div>
                  <Label htmlFor="name">Nama Rule</Label>
                  <Input
                    id="name"
                    data-testid="rule-name-input"
                    placeholder="e.g., WhatsApp Messages to CRM"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="destination_url">Destination URL</Label>
                  <Input
                    id="destination_url"
                    data-testid="destination-url-input"
                    placeholder="https://example.com/webhook"
                    type="url"
                    value={formData.destination_url}
                    onChange={(e) => setFormData({...formData, destination_url: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="http_method">HTTP Method</Label>
                  <Select
                    value={formData.http_method}
                    onValueChange={(value) => setFormData({...formData, http_method: value})}
                  >
                    <SelectTrigger data-testid="http-method-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ORIGINAL">ORIGINAL (follow incoming)</SelectItem>
                      <SelectItem value="POST">POST</SelectItem>
                      <SelectItem value="GET">GET</SelectItem>
                      <SelectItem value="PUT">PUT</SelectItem>
                      <SelectItem value="PATCH">PATCH</SelectItem>
                      <SelectItem value="DELETE">DELETE</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="border-t pt-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <input
                      type="checkbox"
                      id="custom_response_enabled"
                      checked={formData.custom_response_enabled}
                      onChange={(e) => setFormData({...formData, custom_response_enabled: e.target.checked})}
                      className="rounded"
                      data-testid="custom-response-checkbox"
                    />
                    <Label htmlFor="custom_response_enabled" className="cursor-pointer">
                      Enable Custom Response (Optional)
                    </Label>
                  </div>
                  {formData.custom_response_enabled && (
                    <div>
                      <Label htmlFor="custom_response">Custom Response Text</Label>
                      <textarea
                        id="custom_response"
                        className="w-full h-32 p-3 border rounded-lg font-mono text-sm"
                        placeholder='{"status": "success"} or {{hub.challenge}}'
                        value={formData.custom_response}
                        onChange={(e) => setFormData({...formData, custom_response: e.target.value})}
                        data-testid="custom-response-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Response untuk webhook sender. Gunakan <code className="bg-gray-100 px-1 rounded">{`{{key.path}}`}</code> untuk mengambil value dari payload.
                        <br />
                        Contoh: <code className="bg-gray-100 px-1 rounded">{`{{hub.challenge}}`}</code> atau <code className="bg-gray-100 px-1 rounded">{`{"challenge": "{{hub.challenge}}"}`}</code>
                      </p>
                    </div>
                  )}
                </div>
              </TabsContent>
              
              <TabsContent value="conditions" className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg mb-4">
                  <p className="text-sm text-blue-800 mb-2">
                    <AlertCircle className="w-4 h-4 inline mr-2" />
                    <strong>Conditions bersifat OPTIONAL:</strong>
                  </p>
                  <ul className="text-sm text-blue-800 list-disc list-inside space-y-1">
                    <li>Jika tidak ada conditions, webhook akan <strong>langsung di-forward semua</strong></li>
                    <li>Gunakan conditions untuk filter webhook berdasarkan payload</li>
                    <li>Maksimal 7 condition groups dengan AND/OR operator</li>
                    <li>Tip: Gunakan tab "Test Payload" untuk paste JSON dan lihat key paths</li>
                  </ul>
                </div>
                
                {formData.condition_groups.length > 0 && (
                  <div>
                    <Label>Group Logic</Label>
                    <Select
                      value={formData.group_logic}
                      onValueChange={(value) => setFormData({...formData, group_logic: value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="AND">AND (semua groups harus match)</SelectItem>
                        <SelectItem value="OR">OR (salah satu group match)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {formData.condition_groups.length === 0 ? (
                  <div className="text-center py-8 border-2 border-dashed rounded-lg">
                    <p className="text-gray-500 mb-4">Tidak ada conditions - Webhook akan di-forward semua</p>
                    <Button
                      variant="outline"
                      onClick={addConditionGroup}
                      data-testid="add-first-condition-group"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Tambah Condition Group Pertama
                    </Button>
                  </div>
                ) : (
                  <>
                    {formData.condition_groups.map((group, groupIndex) => (
                  <Card key={groupIndex} className="border-2">
                    <CardHeader className="pb-3">
                      <div className="flex justify-between items-center">
                        <CardTitle className="text-sm">Group {groupIndex + 1}</CardTitle>
                        <div className="flex items-center space-x-2">
                          <Select
                            value={group.logic}
                            onValueChange={(value) => updateGroupLogic(groupIndex, value)}
                          >
                            <SelectTrigger className="w-24 h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="AND">AND</SelectItem>
                              <SelectItem value="OR">OR</SelectItem>
                            </SelectContent>
                          </Select>
                          {formData.condition_groups.length > 1 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeConditionGroup(groupIndex)}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {group.conditions.map((condition, condIndex) => (
                        <div key={condIndex} className="flex space-x-2 items-start">
                          <div className="flex-1">
                            <Input
                              placeholder="Key path (e.g., user.name)"
                              value={condition.key_to_parse}
                              onChange={(e) => updateCondition(groupIndex, condIndex, 'key_to_parse', e.target.value)}
                            />
                          </div>
                          <Select
                            value={condition.operator}
                            onValueChange={(value) => updateCondition(groupIndex, condIndex, 'operator', value)}
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="equals">Equals</SelectItem>
                              <SelectItem value="not_equals">Not Equals</SelectItem>
                              <SelectItem value="contains">Contains</SelectItem>
                              <SelectItem value="starts_with">Starts With</SelectItem>
                              <SelectItem value="ends_with">Ends With</SelectItem>
                            </SelectContent>
                          </Select>
                          <Input
                            placeholder="Value"
                            className="flex-1"
                            value={condition.value}
                            onChange={(e) => updateCondition(groupIndex, condIndex, 'value', e.target.value)}
                          />
                          {group.conditions.length > 1 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeCondition(groupIndex, condIndex)}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          )}
                        </div>
                      ))}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => addCondition(groupIndex)}
                        className="w-full"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Tambah Condition
                      </Button>
                    </CardContent>
                  </Card>
                ))}
                
                {formData.condition_groups.length < 7 && (
                  <Button
                    variant="outline"
                    onClick={addConditionGroup}
                    className="w-full"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Tambah Condition Group
                  </Button>
                )}
                </>
              )}
              </TabsContent>
              
              <TabsContent value="test" className="space-y-4">
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    Paste sample JSON payload atau pilih dari captured webhooks untuk melihat key paths
                  </p>
                </div>
                
                {capturedWebhooks.length > 0 && (
                  <div>
                    <Label>Captured Webhooks</Label>
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {capturedWebhooks.map((webhook) => (
                        <Button
                          key={webhook._id}
                          variant="outline"
                          size="sm"
                          className="w-full justify-start"
                          onClick={() => {
                            setSelectedPayload(webhook.payload);
                            setTestPayload(JSON.stringify(webhook.payload, null, 2));
                          }}
                        >
                          {new Date(webhook.created_at).toLocaleString()} - {webhook.method}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
                
                <div>
                  <Label>JSON Payload (Click keys to select path)</Label>
                  {selectedPayload ? (
                    <div className="border rounded-lg p-4 bg-white max-h-96 overflow-auto">
                      <ReactJson
                        src={selectedPayload}
                        theme="rjv-default"
                        enableClipboard={false}
                        displayDataTypes={false}
                        displayObjectSize={false}
                        onSelect={handleJsonSelect}
                      />
                    </div>
                  ) : (
                    <textarea
                      className="w-full h-64 p-4 border rounded-lg font-mono text-sm"
                      value={testPayload}
                      onChange={(e) => setTestPayload(e.target.value)}
                      placeholder='{"key": "value"}'
                    />
                  )}
                </div>
              </TabsContent>
            </Tabs>
            
            <DialogFooter>
              <Button type="button" onClick={handleSubmit} data-testid="save-rule-btn">
                {editingRule ? 'Update Rule' : 'Buat Rule'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Test Rule Dialog */}
        <Dialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
          <DialogContent className="max-w-2xl" data-testid="test-rule-dialog">
            <DialogHeader>
              <DialogTitle>Test Rule: {editingRule?.name}</DialogTitle>
              <DialogDescription>
                Uji rule dengan sample payload
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>JSON Payload</Label>
                <textarea
                  className="w-full h-48 p-4 border rounded-lg font-mono text-sm"
                  value={testPayload}
                  onChange={(e) => setTestPayload(e.target.value)}
                  placeholder='{"key": "value"}'
                  data-testid="test-payload-input"
                />
              </div>
              {testResult && (
                <div className={`p-4 rounded-lg ${testResult.matched ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'} border`}>
                  <p className={`font-medium ${testResult.matched ? 'text-green-800' : 'text-red-800'}`}>
                    {testResult.message}
                  </p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button onClick={handleTestRule} data-testid="run-test-btn">
                <Play className="w-4 h-4 mr-2" />
                Run Test
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent data-testid="delete-rule-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Konfirmasi Hapus Rule</AlertDialogTitle>
              <AlertDialogDescription>
                Apakah Anda yakin ingin menghapus rule <strong>{ruleToDelete?.name}</strong>?
                <br /><br />
                <span className="text-red-600 font-medium">
                  Tindakan ini akan menghapus:
                </span>
                <ul className="list-disc list-inside mt-2 text-red-600">
                  <li>Routing rule</li>
                  <li>Semua webhook logs terkait rule ini</li>
                </ul>
                <br />
                <span className="font-medium">Tindakan ini tidak dapat dibatalkan!</span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="cancel-delete-rule-btn">Batal</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-delete-rule-btn"
              >
                Ya, Hapus Rule
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
